import React, { useState } from 'react'
import { useDropzone } from 'react-dropzone'

import {
  Button,
  Checkbox,
  Fieldset,
  Icon,
  InputUI,
  ModalBox,
  RadioButton,
  Subsection,
  Toasts,
} from '@fourthwall/components'
import { UploadIcon } from '@fourthwall/icons'
import { useFormik } from '@fourthwall/utils/dist/formik'
import { useCreateImageResources } from '@modules/Dashboard/Memberships/hooks/useCreateImageResources'
import { useTiersQuery } from '@modules/Dashboard/Memberships/modules/Perks/hooks/useTiersQuery'
import { getRejectionErrors } from '@utils/dropzone'

import { EmojiPreview } from '../../components/EmojiPreview'
import type { Emoji } from '../../types'
import { validationSchema } from './consts'
import * as S from './styled'
import type { FormValues, PostAccessType } from './types'
import { FormFields } from './types'

type Props = {
  emoji?: Emoji
  onSubmit: (values: FormValues, imageResourceId?: number) => Promise<any>
  onClose: () => void
}

export const CustomEmojiModal = ({ emoji, onSubmit, onClose }: Props) => {
  const { createImageResources, isLoading: isCreateImageResourcesLoading } =
    useCreateImageResources()

  const [isLoading, setIsLoading] = useState(false)
  const [imageResourceId, setImageResourceId] = useState<number | undefined>(undefined)

  const { tiersQuery } = useTiersQuery()

  const form = useFormik<FormValues>({
    initialValues: emoji
      ? {
          [FormFields.image]: emoji.imageUrl,
          [FormFields.text]: emoji.name,
          [FormFields.access]: emoji.accessType.type satisfies PostAccessType,
          [FormFields.tierIds]:
            emoji.accessType.type === 'Tiers' ? emoji.accessType.ids.map((id) => String(id)) : [],
        }
      : {
          [FormFields.image]: '',
          [FormFields.text]: '',
          [FormFields.access]: 'PaidMembers' satisfies PostAccessType,
          [FormFields.tierIds]: [],
        },
    onSubmit: async (values) => {
      try {
        setIsLoading(true)
        await onSubmit(values, imageResourceId)
        setIsLoading(false)
      } catch {
        setIsLoading(false)
      }
    },
    validationSchema,
    enableReinitialize: true,
  })

  const { getRootProps, getInputProps } = useDropzone({
    onDropAccepted: (files) => {
      createImageResources({
        files: [files[0]],
        onSuccess: (images) => {
          form.setFieldValue(FormFields.image, images[0].url)
          setImageResourceId(images[0].id)
        },
      })
    },
    onDropRejected: (rejectedFiles) => {
      getRejectionErrors(rejectedFiles).forEach((rejection) => {
        Toasts.notify(rejection)
      })
    },
    accept: { 'image/*': [] },
    maxSize: 4194304,
    disabled: isCreateImageResourcesLoading,
  })

  const renderTiers = () => {
    if (!tiersQuery.data) return null

    if (form.values.access !== 'Tiers') {
      return null
    }

    return (
      <>
        {tiersQuery.data.tiers.map((tier) => (
          <Fieldset.Row inner>
            <Checkbox
              {...form.getFieldProps(FormFields.tierIds)}
              value={String(tier.id)}
              native
              checked={form.values.tierIds.includes(String(tier.id))}
              label={tier.name}
            />
          </Fieldset.Row>
        ))}
      </>
    )
  }

  return (
    <ModalBox
      title={emoji ? 'Edit custom emoji' : 'Add custom emoji'}
      size="small"
      buttons={[
        <Button label="Cancel" type="button" appearance="secondary" onClick={onClose} />,
        <Button
          label={emoji ? 'Save' : 'Add custom emoji'}
          appearance="primary"
          onClick={form.submitForm}
          disabled={emoji ? !form.dirty : false}
          loading={isLoading}
        />,
      ]}
      onClose={onClose}
    >
      <Subsection title="Image">
        <Fieldset>
          <Fieldset.Row>
            Square images under 128KB with transparent backgrounds work best. If your image is too
            large, we’ll resize it for you.
          </Fieldset.Row>
          <Fieldset.Row>
            <S.ImageFieldContainer>
              <EmojiPreview size="large" url={form.values.image} />
              <S.ImageFieldInfo>
                <S.ImageFieldName>Emoji image</S.ImageFieldName>
                <Button
                  {...getRootProps()}
                  label="Upload image"
                  appearance="secondary"
                  size="small"
                  leftIcon={<Icon component={UploadIcon} height={16} />}
                  loading={isCreateImageResourcesLoading}
                />
                <input {...getInputProps()} type="file" hidden />
              </S.ImageFieldInfo>
            </S.ImageFieldContainer>
          </Fieldset.Row>
          {form.errors.image && <InputUI.Error>{form.errors.image}</InputUI.Error>}
        </Fieldset>
      </Subsection>
      <Subsection title="Text">
        <InputUI.Root>
          <InputUI.Box $invalid={!!form.errors.text}>
            {form.values.text && (
              <S.InputPrefixAndSuffix>{form.values.text}</S.InputPrefixAndSuffix>
            )}
            <InputUI.Container>
              <InputUI.Input
                {...form.getFieldProps(FormFields.text)}
                $inputSize="large"
                type="text"
                placeholder=":emojiname:"
              />
            </InputUI.Container>
          </InputUI.Box>
          {form.errors.text && <InputUI.Error>{form.errors.text}</InputUI.Error>}
        </InputUI.Root>
      </Subsection>
      <Subsection title="Who can use this emoji?">
        <Fieldset>
          <Fieldset.Row>
            <RadioButton
              {...form.getFieldProps(FormFields.access)}
              checkedValue={'PaidMembers' satisfies PostAccessType}
              label="Members only"
            />
          </Fieldset.Row>
          <Fieldset.Row>
            <RadioButton
              {...form.getFieldProps(FormFields.access)}
              checkedValue={'FreeMembers' satisfies PostAccessType}
              label="Free accounts only"
            />
          </Fieldset.Row>
          <Fieldset.Row>
            <RadioButton
              {...form.getFieldProps(FormFields.access)}
              checkedValue={'FreeAndPaidMembers' satisfies PostAccessType}
              label="Members & free accounts"
            />
          </Fieldset.Row>
          <Fieldset.Row>
            <RadioButton
              {...form.getFieldProps(FormFields.access)}
              checkedValue={'Tiers' satisfies PostAccessType}
              label="Selected tiers"
            />
          </Fieldset.Row>
          {renderTiers()}
          {form.errors.tierIds && <InputUI.Error>{form.errors.tierIds}</InputUI.Error>}
        </Fieldset>
      </Subsection>
    </ModalBox>
  )
}
